'use client';

import React, { useEffect, useState } from 'react';
import { AutoComplete, Input, Spin } from 'antd';
import { tv } from 'tailwind-variants';
import { CaretDown } from '@phosphor-icons/react';

const selectStyle = tv({
  base: 'rounded-md !test-base autoCompleteCommon',
  variants: {
    statusSelect: {
      default: '',
      disabled: 'bg-[var(--bgBlackOpacity)] text-gray-400 cursor-not-allowed',
      loading: 'animate-pulse',
    },
    bg: {
      default: '!text-black',
    },
    sizeSelect: {
      xs: 'h-[24px]',
      sm: 'h-[32px]',
      md: 'w-full',
      lg: 'h-[48px]',
    },
  },
  defaultVariants: {
    bg: 'default',
    statusSelect: 'default',
    sizeSelect: 'md',
  },
});

type Option = {
  label: string;
  value: string | number;
};

type Props = {
  value?: string | number;
  onChange?: (value: string | number) => void;
  onSelect?: (value: string | number) => void;
  options?: Option[];
  placeholderSelect?: string;
  loading?: boolean;
  disabled?: boolean;
  className?: string;
  widthSelect?: string;
  sizeSelect?: 'xs' | 'sm' | 'md' | 'lg';
  statusSelect?: 'default' | 'disabled' | 'loading';
  bg?: 'default';
};

const AutoCompleteCommon: React.FC<Props> = ({
  value,
  onChange,
  onSelect,
  options = [],
  placeholderSelect = 'Nhập hoặc chọn',
  loading,
  disabled,
  className = '',
  widthSelect,
  sizeSelect,
  statusSelect,
  bg,
}) => {
  const [inputValue, setInputValue] = useState('');

  useEffect(() => {
    if (value === null || value === undefined) {
      if (inputValue !== '') setInputValue('');
      return;
    }

    const matched = options.find((opt) => opt.value === value);

    if (matched) {
      if (matched.label !== inputValue) {
        setInputValue(matched.label);
      }
    } else if (typeof value === 'string' || typeof value === 'number') {
      if (String(value) !== inputValue) {
        setInputValue(String(value));
      }
    }
  }, [value, options]);

  const mergedClassName = `${selectStyle({
    statusSelect,
    sizeSelect,
    bg,
  })} ${className}`;

  const handleSelect = (val: string, option: Option) => {
    setInputValue(option.label);
    onSelect?.(option.value); // Trả về id
  };

  const handleChange = (val: string) => {
    setInputValue(val);
    const matched = options.find((opt) => opt.label === val);
    if (matched) {
      onChange?.(matched.value);
    } else {
      onChange?.(val); // Trả về tự do
    }
  };
  return (
    <AutoComplete
      value={inputValue}
      onChange={handleChange}
      onSelect={handleSelect}
      options={options}
      style={{
        width: '100%',
        minWidth: widthSelect,
        fontSize: '14px',
      }}
      placeholder={placeholderSelect}
      className={mergedClassName + 'h-[30px] xl:h-[36px]'}
      disabled={disabled}
      notFoundContent={loading ? <Spin size='small' /> : 'Không có kết quả'}
      filterOption={(inputValue, option) => (option?.label as string)?.toLowerCase().includes(inputValue.toLowerCase())}
    >
      <Input allowClear className='h-[30px] xl:h-[36px]' suffix={<CaretDown size={20} />} />
    </AutoComplete>
  );
};

export default AutoCompleteCommon;
